import { useState } from 'react';
import { Button, Input, Modal, Typography, theme } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
  useCan,
  useDelete,
  useNavigation,
  useTranslate,
} from '@refinedev/core';
import type { UserOut } from '@/api/generated/schemas';

const { Paragraph, Text } = Typography;

interface DeleteUserButtonProps {
  record: UserOut;
}

/**
 * Delete button for the user show page. Opens a modal that requires typing
 * the user's email (or username when the account has no email) before the
 * soft-delete request is sent.
 */
export const DeleteUserButton = ({ record }: DeleteUserButtonProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { list } = useNavigation();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');

  const { data: canDelete } = useCan({
    resource: 'iam.users',
    action: 'delete',
    params: { id: record.id },
  });

  const {
    mutate: deleteUser,
    mutation: { isPending },
  } = useDelete();

  if (!canDelete?.can) return null;

  const expected = record.email ?? record.username;
  const identifierKind = record.email ? 'email' : 'username';
  const matches = typed.trim() === expected;

  const close = () => {
    setOpen(false);
    setTyped('');
  };

  const handleDelete = () => {
    if (!matches) return;
    deleteUser(
      {
        resource: 'iam.users',
        id: record.id,
        successNotification: {
          type: 'success',
          message: t('notifications.success'),
          description: t('iam.users.actions.delete_success'),
        },
      },
      {
        onSuccess: () => {
          close();
          list('iam.users');
        },
      }
    );
  };

  return (
    <>
      <Button danger icon={<DeleteOutlined />} onClick={() => setOpen(true)}>
        {t('buttons.delete')}
      </Button>

      <Modal
        open={open}
        title={t('iam.users.actions.delete_title')}
        okText={t('buttons.delete')}
        cancelText={t('buttons.cancel')}
        okButtonProps={{ danger: true, disabled: !matches }}
        confirmLoading={isPending}
        onOk={handleDelete}
        onCancel={close}
        destroyOnClose
      >
        <Paragraph>{t('iam.users.actions.delete_description')}</Paragraph>
        <Paragraph>
          {t(`iam.users.actions.delete_type_${identifierKind}`)}{' '}
          <Text strong copyable>
            {expected}
          </Text>
        </Paragraph>
        <Input
          autoFocus
          value={typed}
          placeholder={expected}
          onChange={(e) => setTyped(e.target.value)}
          onPressEnter={handleDelete}
          status={typed && !matches ? 'error' : undefined}
          style={{ marginTop: token.marginXS }}
        />
      </Modal>
    </>
  );
};
